import React, { useContext, useEffect, useRef, useState } from "react";
import { Card, Text, Grid, Button, Badge, Group, Title, Loader } from "@mantine/core";
import { IconShare } from "@tabler/icons-react";
import fetchApi from "../Lib/api";

// Add CSS for hover effect
const cardStyles = `
  .showcase-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15) !important;
  }
`;

// Helper function to truncate text
const truncateText = (text, maxLength = 80) => {
  if (!text) return "";
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength).trim() + "...";
};

export function ShowcaseSection({ items = [] }) {
  const [showAll, setShowAll] = useState(false);
  const [followStates, setFollowStates] = useState({});
  const [loadingStates, setLoadingStates] = useState({});
  const visibleItems = showAll ? items : items.slice(0, Math.min(items.length, 3));

  // Initialize follow states from props
  useEffect(() => {
    const initialFollowStates = {};
    items.forEach(item => {
      if (item.isFollowing !== undefined) {
        initialFollowStates[item.id] = item.isFollowing;
      }
    });
    setFollowStates(initialFollowStates);
  }, []);

  // Inject CSS for hover effects
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = cardStyles;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);
  
  // Follow/Unfollow handler
  const handleFollowUnfollow = async (isFollowing, slug, projectId) => {
    try {
      setLoadingStates(prev => ({ ...prev, [projectId]: true }));
      
      const endpoint = !isFollowing
        ? `/startup_entities/${slug}/followings`
        : `/startup_entities/${slug}/followings/${slug}`;
      const method = !isFollowing ? "POST" : "DELETE";
      const response = await fetchApi(endpoint, method);

      if (response.ok) {
        const res = await response.json();
        setFollowStates(prev => ({ ...prev, [projectId]: !isFollowing }));
      } else {
        const { error } = await response.json();
        console.error(error);
      }
    } catch (error) {
      console.error("Error during follow/unfollow:", error);
    } finally {
      setLoadingStates(prev => ({ ...prev, [projectId]: false }));
    }
  };
  
  // Debug: uncomment to check if items are being passed correctly
  // console.log('ShowcaseSection items:', items)
  // console.log('ShowcaseSection items length:', items?.length)
  // console.log('ShowcaseSection visibleItems:', visibleItems)
  
  if (!items || items.length === 0) {
    return (
      <Card shadow="none" withBorder={false} radius="0" aria-labelledby="showcase-heading" p="md">
        <Title order={4} id="showcase-heading" mb="sm">
          Showcase
        </Title>
        <Text>
          No Projects Found
        </Text>
      </Card>
    );
  }

  return (
    <Card shadow="none" withBorder={false} radius="0" aria-labelledby="showcase-heading" p="lg" pt="md">
      <Title order={4} id="showcase-heading" mb="sm">
        Showcase
      </Title>
      <Grid>
        {visibleItems.map((item) => (
          <Grid.Col span={12} sm={6} lg={4} key={item.id}>
            <Card
              shadow="xs"
              radius="md"
              withBorder
              className="showcase-card"
              style={{ 
                height: "28rem", 
                overflow: "hidden", 
                transition: "all 0.3s ease",
                cursor: "pointer"
              }}
              onClick={() => window.location.href = `/projects/${item.slug || '#'}`}
            >
              <div style={{ position: "relative", height: "13rem", backgroundColor: "#e5e7eb" }}>
                {item.imageUrl ? (
                  <img
                    src={item.imageUrl}
                    alt={`${item.title} logo`}
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                ) : (
                  <div style={{ 
                    width: "100%", 
                    height: "100%", 
                    display: "flex", 
                    alignItems: "center", 
                    justifyContent: "center",
                    fontSize: "2.5rem",
                    color: "#9ca3af"
                  }}>
                    {item.title?.charAt(0)?.toUpperCase() || "S"}
                  </div>
                )}

                <Group spacing="sm" style={{ position: "absolute", left: 16, top: 16 }} wrap="wrap">
                  {item.industry && (
                    <Badge color="gray" variant="light" size="sm">
                      {item.industry}
                    </Badge>
                  )}
                  {item.location && (
                    <Badge color="gray" variant="light" size="sm">
                      {item.location}
                    </Badge>
                  )}
                </Group>

                {/* <Button
                  variant="subtle"
                  size="sm"
                  radius="sm"
                  style={{
                    position: "absolute",
                    right: 16,
                    top: 16,
                    backgroundColor: "rgba(255,255,255,0.9)",
                  }}
                >
                  <IconShare size={18} color="gray" />
                </Button> */}
              </div>

              <Card.Section inheritPadding py="lg">
                <Text fw={500}>
                  {item.title}
                </Text>
                {item.subtitle && (
                  <Text lineClamp={2}>
                    {truncateText(item.subtitle, 60)}
                  </Text>
                )}
                {(item.role || item.since) && (
                  <Text mt="sm">
                    {item.role} {item.role && item.since ? " • " : ""}{" "}
                    {item.since ? `Since ${item.since}` : ""}
                  </Text>
                )}
                <Button 
                  size="sm" 
                  mt="xl" 
                  color={followStates[item.id] ? "gray" : "blue"}
                  variant={followStates[item.id] ? "outline" : "filled"}
                  loading={loadingStates[item.id]}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleFollowUnfollow(followStates[item.id], item.slug, item.id);
                  }}
                  disabled={loadingStates[item.id]}
                >
                  {followStates[item.id] ? "Unfollow" : "Follow"}
                </Button>
              </Card.Section>
            </Card>
          </Grid.Col>
        ))}
      </Grid>

      {items.length > 3 && (
        <Group position="center" mt="lg">
          <Button variant="outline" size="sm" onClick={() => setShowAll((v) => !v)}>
            {showAll ? "Show less" : "View all"}
          </Button>
        </Group>
      )}
    </Card>
  );
}
